import React from 'react';
import { Box, Typography, Avatar, IconButton, Chip, Tooltip } from '@mui/material';
import { MoreVert as MoreVertIcon, LinkedIn as LinkedInIcon } from '@mui/icons-material';
import type { LeadListItem } from '@/api/leads';

interface LeadRowProps {
  item: LeadListItem;
  onRowClick: (uuid: string) => void;
}

export const LeadRow: React.FC<LeadRowProps> = ({ item, onRowClick }) => {
  const p = item.prospect;
  const image = typeof p?.profile_image === 'string' ? p?.profile_image : p?.profile_image?.url;
  const linkedin = p?.linkedin_public_url || p?.linkedin_url || '';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '2.1fr 1.4fr 1fr 1.3fr 1.8fr 2.3fr 0.6fr',
        gap: 1,
        alignItems: 'center',
        py: 1.5,
      }}
    >
      {/* Prospect */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          cursor: 'pointer',
        }}
        onClick={() => onRowClick(p?.key || item.key)}
      >
        <Avatar src={image || undefined} sx={{ width: 32, height: 32 }} />
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
            noWrap
            title={p?.full_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim()}
          >
            {p?.full_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || 'Unknown'}
          </Typography>
          {linkedin && (
            <Tooltip title="Open LinkedIn">
              <IconButton
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                size="small"
                color="primary"
                onClick={(e) => e.stopPropagation()}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Title */}
      <Typography variant="body2" noWrap title={p?.title || ''}>
        {p?.title || '-'}
      </Typography>

      {/* Location */}
      <Typography variant="body2" color="text.secondary" noWrap title={p?.location || ''}>
        {p?.location || '-'}
      </Typography>

      {/* Company */}
      <Typography variant="body2" noWrap title={p?.current_company?.name || ''}>
        {p?.current_company?.name || '-'}
      </Typography>

      {/* Step Categories */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
        {(item.step_categories || []).map((c) => (
          <Chip key={c} label={c} size="small" variant="outlined" />
        ))}
      </Box>

      {/* Activities Summary */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', whiteSpace: 'pre-line' }}
      >
        {item.activity_summary || '-'}
      </Typography>

      {/* Actions */}
      <Box sx={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
        <IconButton size="small">
          <MoreVertIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

